import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { StudentInfoCard } from "../components/students/StudentInfoCard";
import { EnrollmentsTable } from "../components/students/EnrollmentsTable";
import { DeviceLoginTable } from "../components/students/DeviceLoginTable";
import { CertificatesTable } from "../components/students/CertificatesTable";
import { CertificateRequestsTable } from "../components/students/CertificateRequestsTable";
import { userService, User } from "../services/userService";
import { enrollmentService } from "../services/enrollmentService";
import { EditUserDialog } from "../components/users/EditUserDialog";
import { DeleteUserDialog } from "../components/users/DeleteUserDialog";
import { DeleteDeviceDialog } from "../components/students/DeleteDeviceDialog";
import { BlockDeviceDialog } from "../components/students/BlockDeviceDialog";
import { EditEnrollmentStatusDialog } from "../components/enrollments/EditEnrollmentStatusDialog";
import { ExtendCoursesDialog } from "../components/students/ExtendCoursesDialog";

export default function StudentDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<User | null>(null);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleteDeviceOpen, setIsDeleteDeviceOpen] = useState(false);
  const [isBlockDeviceOpen, setIsBlockDeviceOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  const [isEditEnrollmentOpen, setIsEditEnrollmentOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<any>(null);
  const [isExtendOpen, setIsExtendOpen] = useState(false);

  const fetchData = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      // 1. Fetch User Details
      const userRes = await userService.getUserById(id);
      setStudent(userRes.user);

      // 2. Fetch Enrollments
      const enrollRes = await enrollmentService.getEnrollmentsByUser(id);
      setEnrollments(enrollRes.enrollments || []);

      // 3. Fetch Devices
      const deviceRes = await userService.getUserDevices(id);
      setDevices(deviceRes.devices || []);
    } catch (err) {
      console.error("Failed to fetch student details:", err);
      setError("Failed to load student data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleIssueCertificate = async () => {
    // Placeholder for issue certificate logic
    alert("This feature requires backend support for admin issuance.");
  };

  const handleEditEnrollment = (enrollment: any) => {
    setSelectedEnrollment(enrollment);
    setIsEditEnrollmentOpen(true);
  };

  const handleDeleteDevice = async (deviceId: string) => {
    const device = devices.find((d) => d.id === deviceId);
    if (!device) return;
    setSelectedDevice(device);
    setIsDeleteDeviceOpen(true);
  };

  const handleBlockDevice = async (deviceId: string) => {
    const device = devices.find((d) => d.id === deviceId);
    if (!device) return;
    setSelectedDevice(device);
    setIsBlockDeviceOpen(true);
  };

  const confirmDeleteDevice = async () => {
    if (!selectedDevice) return;
    await userService.deleteDevice(selectedDevice.id);
    // Refresh devices list
    const deviceRes = await userService.getUserDevices(id!);
    setDevices(deviceRes.devices || []);
  };

  const confirmBlockDevice = async (reason: string) => {
    if (!selectedDevice) return;
    await userService.blockDevice(selectedDevice.id, reason);
    // Refresh devices list
    const deviceRes = await userService.getUserDevices(id!);
    setDevices(deviceRes.devices || []);
  };

  const filteredEnrollments = enrollments;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !student) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-destructive font-medium">
            Error: {error || "Student not found"}
          </div>
          <button
            onClick={() => navigate("/students")}
            className="mt-4 text-primary hover:underline"
          >
            Back to Students
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-8">
        {/* 1. Basic Info */}
        <StudentInfoCard
          student={student}
          onEdit={() => setIsEditOpen(true)}
          onDelete={() => setIsDeleteOpen(true)}
          onExtend={() => setIsExtendOpen(true)}
        />

        {/* 2. Enrollment Courses */}
        <EnrollmentsTable
          enrollments={filteredEnrollments}
          onIssueCertificate={handleIssueCertificate}
          onEdit={handleEditEnrollment}
        />

        {/* 3. Device Login */}
        <DeviceLoginTable
          devices={devices}
          onDelete={handleDeleteDevice}
          onBlock={handleBlockDevice}
          onUnblock={async (deviceId) => {
            try {
              await userService.unblockDevice(deviceId);
              const deviceRes = await userService.getUserDevices(id!);
              setDevices(deviceRes.devices || []);
            } catch (err) {
              console.error("Failed to unblock device:", err);
              alert("Failed to unblock device.");
            }
          }}
        />

        {/* 4. Certificate Section */}
        <CertificatesTable
          enrollments={enrollments} // Pass all, table filters internally for certificates
          onIssueCertificate={handleIssueCertificate}
        />

        {/* 5. Certificate Requests Section */}
        <CertificateRequestsTable studentId={id!} onRefresh={fetchData} />

        {/* Dialogs */}
        <EditUserDialog
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            fetchData();
            setIsEditOpen(false);
          }}
          user={student}
          canChangeRole={true}
          allowedRoles={["student"]}
        />

        <DeleteUserDialog
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onSuccess={() => {
            setIsDeleteOpen(false);
            navigate("/students");
          }}
          user={student}
        />

        <EditEnrollmentStatusDialog
          isOpen={isEditEnrollmentOpen}
          onClose={() => {
            setIsEditEnrollmentOpen(false);
            setSelectedEnrollment(null);
          }}
          onSuccess={() => {
            fetchData();
            setIsEditEnrollmentOpen(false);
            setSelectedEnrollment(null);
          }}
          enrollment={selectedEnrollment}
        />

        <ExtendCoursesDialog
          isOpen={isExtendOpen}
          onClose={() => setIsExtendOpen(false)}
          onSuccess={() => {
            fetchData();
            setIsExtendOpen(false);
          }}
          student={student}
          enrollments={filteredEnrollments}
        />

        {selectedDevice && (
          <>
            <DeleteDeviceDialog
              isOpen={isDeleteDeviceOpen}
              onClose={() => {
                setIsDeleteDeviceOpen(false);
                setSelectedDevice(null);
              }}
              onConfirm={confirmDeleteDevice}
              deviceName={selectedDevice.deviceName}
            />

            <BlockDeviceDialog
              isOpen={isBlockDeviceOpen}
              onClose={() => {
                setIsBlockDeviceOpen(false);
                setSelectedDevice(null);
              }}
              onConfirm={confirmBlockDevice}
              deviceName={selectedDevice.deviceName}
            />
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
